import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import HelpDot from '@/components/ui/HelpDot';
import Spinner from '@/components/ui/Spinner';
import type { RosterEntry, RosterKind } from '@/hooks/useProject';

/**
 * The cast, sets and hero props a project reuses.
 *
 * Characters, locations and props are the same interaction — a list of named things,
 * each either LOCKED to an uploaded reference sheet (reused verbatim by every future
 * production) or left to be designed fresh each time. This was three copies of the
 * same 140-line block; it is now one component configured three ways.
 */
export default function AssetRoster({
  kind,
  title,
  icon,
  help,
  addPlaceholder,
  emptyHint,
  projectId,
  entries,
  onToggle,
  onRemove,
  onUpload,
}: {
  kind: RosterKind;
  title: string;
  icon: React.ReactNode;
  /** Plain-language explanation of what locking this kind of thing does. */
  help: string;
  addPlaceholder: string;
  emptyHint: string;
  projectId: string;
  entries: RosterEntry[];
  onToggle: (kind: RosterKind, name: string, enabled: boolean) => void;
  onRemove: (kind: RosterKind, name: string) => void;
  onUpload: (kind: RosterKind, name: string, file: File) => Promise<void>;
}) {
  const { t } = useTranslation('dashboard');
  const fileRef = useRef<HTMLInputElement>(null);
  const targetRef = useRef<string>('');
  const [manualName, setManualName] = useState('');
  const [uploading, setUploading] = useState<string | null>(null);

  const pickFor = (name: string) => {
    targetRef.current = name;
    fileRef.current?.click();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const name = targetRef.current.trim();
    e.target.value = '';
    if (!file || !name) return;
    setUploading(name);
    try {
      await onUpload(kind, name, file);
      setManualName('');
    } finally {
      setUploading(null);
      targetRef.current = '';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          {icon}
          {title}
          <HelpDot>{help}</HelpDot>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* One hidden picker serves every row; the row sets the target name first. */}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} />

        <div className="mb-3 flex max-w-md items-center gap-1.5">
          <input
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary"
            placeholder={addPlaceholder}
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
          />
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0 px-2 text-[10px]"
            disabled={!!uploading || !manualName.trim()}
            onClick={() => pickFor(manualName.trim())}
          >
            <Upload className="mr-1 h-3 w-3" />
            {t('roster.uploadSheet')}
          </Button>
        </div>

        {entries.length === 0 ? (
          <p className="text-xs leading-relaxed text-faint">{emptyHint}</p>
        ) : (
          // A grid, not a stack: these sit in a full-width section now, and a single
          // column of portrait sheets was what made the old page so tall.
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {entries.map((e) => {
              const imgSrc = `/api/projects/${projectId}/${kind}/${encodeURIComponent(e.name)}/image`;
              return (
                <div key={e.name} className="overflow-hidden rounded-lg border border-border">
                  {e.locked && (
                    <img
                      src={imgSrc}
                      alt={e.name}
                      title={t('roster.viewFullSize')}
                      className="w-full cursor-zoom-in bg-black object-contain"
                      onClick={() => window.open(imgSrc, '_blank')}
                    />
                  )}
                  <div className="flex items-center gap-2 p-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-xs font-medium">{e.name}</span>
                        <Badge variant={e.locked ? 'success' : 'secondary'} className="px-1 text-[9px]">
                          {e.locked ? t('roster.locked') : t('roster.notLocked')}
                        </Badge>
                        {e.locationId && (
                          <Badge variant="outline" className="px-1 font-mono text-[9px]">
                            {e.locationId}
                          </Badge>
                        )}
                        {e.inScript === false && (
                          <Badge variant="outline" className="px-1 text-[9px]">
                            {t('roster.notInScript')}
                          </Badge>
                        )}
                      </div>
                      <div className="truncate text-[10px] text-faint">
                        {e.locked
                          ? `${e.voiceName ? t('roster.voice', { name: e.voiceName }) + ' • ' : ''}` +
                            `${e.enabled ? t('roster.toggledOn') : t('roster.toggledOff')}`
                          : e.description || t('roster.freshEachTime')}
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      {e.locked ? (
                        <>
                          {/* On/Off is a two-state toggle, so the SELECTED side has
                              to carry the colour: gold for in-use, red for excluded.
                              Red reads as "this won't be used" without needing to
                              parse which of the two buttons is filled in. */}
                          <Button
                            variant={e.enabled ? 'default' : 'outlinePrimary'}
                            size="sm"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => onToggle(kind, e.name, true)}
                          >
                            {t('action.on', { ns: 'common' })}
                          </Button>
                          <Button
                            variant={!e.enabled ? 'destructive' : 'outlineDestructive'}
                            size="sm"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => onToggle(kind, e.name, false)}
                          >
                            {t('action.off', { ns: 'common' })}
                          </Button>
                          <button
                            className="p-1 text-faint transition-colors hover:text-destructive"
                            title={t('roster.removeTitle', { name: e.name })}
                            onClick={() => onRemove(kind, e.name)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px]"
                          disabled={!!uploading}
                          onClick={() => pickFor(e.name)}
                        >
                          {uploading === e.name ? <Spinner /> : <Upload className="mr-1 h-3 w-3" />}
                          {t('roster.uploadSheet')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
